import {
  Component,
  EventEmitter,
  Inject,
  Input, LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ILabels } from "../../../../libraries/interfaces/ILabels";
import { Yii } from "../../../../libraries/interfaces/Yii";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import {
  IQuotationBlock,
  IQuotationSection,
  IQuotationsQuotation,
  IQuotationXrates
} from "../../../../libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import {
  QuotationQuotationsResourceService
} from "../../../../libraries/service/resources/quotation-quotations/quotation-quotations-resource.service";
import { Subscription } from 'rxjs';
import { BtnStyleTypes } from "../../../../libraries/ui_button/btn.component";
import { NotificationType } from "../../../../libraries/interfaces/INotification";

interface IExchangeRateInformation {
  currency: string;
  rateUsed: string;
  rateAvailable: string[];
}

@Component({
  selector: 'exchange-rate-information',
  templateUrl: 'exchange-rate-information.component.html',
  styleUrls: ['exchange-rate-information.component.scss']
})
export class ExchangeRateInformationComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public quotation: IQuotationsQuotation;

  @Output()
  public readonly refreshExchangeRateEvent: EventEmitter<void> = new EventEmitter<void>();

  public wording: ILabels = {
    currency: Yii.t('wui-quotation-js', 'Devise'),
    rateUsed: Yii.t('wui-quotation-js', 'Taux utilisé'),
    rateAvailable: Yii.t('wui-quotation-js', 'Taux actuel disponible'),
    displayExchangeRateTable: Yii.t('wui-quotation-js', 'Afficher les taux de change'),
    hideExchangeRateTable: Yii.t('wui-quotation-js', 'Masquer les taux de change'),
    exchangeRateDataStatusProgress: Yii.t('wui-quotation-js', 'Récupération des taux de change en cours...'),
    exchangeRateDataStatusUpToDate: Yii.t('wui-quotation-js', 'Les taux utilisés sur votre cotation sont à jour'),
    exchangeRateDataStatusNew: Yii.t('wui-quotation-js', 'De nouveaux taux de change sont disponibles pour votre cotation'),
    exchangeRateDataStatusUnavailable: Yii.t('wui-quotation-js', 'Certains taux de change nécessaire à votre cotation sont indisponibles'),
    refreshExchangeRate: Yii.t('wui-quotation-js', 'Mettre à jour les taux'),
    noExchangeRateTableData: Yii.t('wui-quotation-js', 'Aucun taux de change n\'est appliqué'),
  };
  public displayedColumns: string[] = ['currency', 'rateUsed', 'rateAvailable'];
  public readonly chevronUp: IconDefinition = faChevronUp;
  public readonly chevronDown: IconDefinition = faChevronDown;
  public readonly notAvailable: string = 'N/A';
  public readonly refreshButtonType: BtnStyleTypes = BtnStyleTypes.actionLink;
  public readonly notificationType: typeof NotificationType = NotificationType;

  public exchangeRateDataStatus: string;
  public isExchangeRateDataStatusError: boolean;
  public isDisplayExchangeRateTable: boolean = false;
  public exchangeRateTableData: IExchangeRateInformation[] = [];
  public locale: string = null;
  public isOneDifference: boolean = false;
  private exchangeRateData: IQuotationXrates[] = null;
  private currencyInQuotation: string[] = [];
  private exchangeRateDataSubscription: Subscription;

  constructor(
      private readonly quotationQuotationsResourceService: QuotationQuotationsResourceService,
      @Inject(LOCALE_ID) locale: string
  ) {
    this.locale = locale;
  }

  public ngOnInit(): void {
    this.initExchangeRateData();
    this.initExchangeRateInformation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.quotation && !changes.quotation.firstChange) {
      this.initExchangeRateInformation();
    }
  }

  public ngOnDestroy(): void {
    this.exchangeRateDataSubscription?.unsubscribe();
  }

  public refreshExchangeRate(): void {
    this.refreshExchangeRateEvent.emit();
  }

  private initExchangeRateDataStatus(): void {
    this.exchangeRateDataStatus = this.wording.exchangeRateDataStatusProgress;
  }

  private initExchangeRateInformation(): void {
    this.isOneDifference = false;
    this.initExchangeRateDataStatus();
    this.initCurrencyInQuotation();
    this.initExchangeRateTableData();
  }

  private initExchangeRateData(): void {
    const target: string = this.quotation.cost.currency;
    this.exchangeRateDataSubscription = this.quotationQuotationsResourceService.getExchangeRates({target, trip:{begin:this.quotation.begin}})
        .subscribe((res: IQuotationXrates[]): void => {
          this.exchangeRateData = res;
          this.initExchangeRateInformation();
        });
  }

  private initCurrencyInQuotation(): void {
    this.currencyInQuotation = [];
    // forEach all currency in quotation
    this.quotation?.sections?.forEach((section: IQuotationSection): void => {
      section?.blocks?.forEach((block: IQuotationBlock): void => {
        const currency: string = block?.totalPrices?.length === 1 ? block.totalPrices[0].cost?.currency : null;
        if (currency && currency !== this.quotation?.cost?.currency && !this.currencyInQuotation.includes(currency)) {
          this.currencyInQuotation.push(currency);
        }
      });
    });
  }

  private initExchangeRateTableData(): void {
    this.exchangeRateTableData = [];
    this.quotation?.details?.xrates?.map((xRate: IQuotationXrates): void => {
      if (this.currencyInQuotation.includes(xRate.source)) {
        this.currencyInQuotation.splice(this.currencyInQuotation.indexOf(xRate.source), 1);
        const rateAvailable: IQuotationXrates = this.getRateAvailableById(xRate.id);
        this.exchangeRateTableData.push({
          currency: `${xRate.source} => ${xRate.target}`,
          rateUsed: xRate.rate,
          rateAvailable: rateAvailable ? this.makeRateAvailable(+xRate.rate, +rateAvailable.rate) : [this.notAvailable]
        });
      }
    });
    if (this.currencyInQuotation.length !== 0) {
      this.currencyInQuotation.forEach((exchangeRateInQuotation: string): void => {
          this.exchangeRateTableData.push({
            currency: `${exchangeRateInQuotation} => ${this.quotation.cost.currency}`,
            rateUsed: this.notAvailable,
            rateAvailable: [this.notAvailable]
          });
      });
      this.exchangeRateDataStatus = `${this.wording.exchangeRateDataStatusUnavailable} (${this.currencyInQuotation.join()})`;
    }
    this.checkExchangeRateDataStatusError();
  }

  private checkExchangeRateDataStatusError(): void {
    this.isExchangeRateDataStatusError = this.exchangeRateDataStatus !== this.wording.exchangeRateDataStatusProgress;
    if (!this.isExchangeRateDataStatusError) {
      this.exchangeRateDataStatus = this.wording.exchangeRateDataStatusUpToDate;
    }
  }

  private getRateAvailableById(xRateId: number): IQuotationXrates {
    return this.exchangeRateData?.find((exchangeRate: IQuotationXrates): boolean => exchangeRate.id === xRateId);
  }

  private makeRateAvailable(rateUsed: number, rateAvailable: number): string[] {
    const difference: number = Math.round((rateAvailable - rateUsed) * 1000000) / 1000000;
    const rateAvailableMake: string[] = [rateAvailable.toString()];
    if (difference !== 0) {
      this.isOneDifference = true;
      this.exchangeRateDataStatus = this.wording.exchangeRateDataStatusNew;
      rateAvailableMake.push(difference.toString());
    }
    return rateAvailableMake;
  }

}
